import { useNavigate, useParams } from 'react-router-dom';
import { Button, Form, Space } from 'antd';
import { useMaterialRequestStore } from '../../../../../store/MaterialRequestStore';
import React, { useEffect, useState } from 'react';
import {
  useFetchData,
  useFetchFile,
  useUpdateData,
} from '../../../../../hooks';
import { MaterialRequestRecord } from '../../../../../types/MaterialRequest';
import {
  MRS_API,
  useDeleteMRFile,
  useUploadMRFiles,
} from '../../../../../hooks/useMaterialRequestHooks';
import { BaseModel, FormField, PageableResponse } from '../../../../../types';
import { Resource } from '../../../../../lib/enums';
import { v4 as uuidv4 } from 'uuid';
import dayjs from 'dayjs';
import { dateFormat } from '../../../../../lib/utils';
import PageHeaderAction from '../../../../../components/PageHeaderAction';
import SubmitButton from '../../../../../components/ui/SubmitButton';
import CustomForm from '../../../../../components/ui/CustomForm';
import ErrorModal from '../../../../../components/ui/modal/ErrorModal';
import InfoModal from '../../../../../components/ui/modal/InfoModal';
import MaterialRequestMovementEdit from './MaterialRequestMovementEdit';
import MaterialRequestLoadingView from '../../info/MaterialRequestLoadingView';

const MaterialRequestFormEditPage = () => {
  const params = useParams();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const materialRequestStore = useMaterialRequestStore();
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
  const [errorMess, setErrorMess] = useState<string | undefined>('');

  const { isLoading, data, isError, error } =
    useFetchData<MaterialRequestRecord>(`${MRS_API}/${params.id}`);

  const {
    mutate: mutateFile,
    isError: isFileError,
    error: fileError,
  } = useUploadMRFiles();
  const { data: dataFiles } = useFetchFile(
    `${MRS_API}/${params.id}/files`,
    'api/stock/mrs',
  );
  const mutationDelete = useDeleteMRFile();
  const {
    mutate: mutate,
    isSuccess: isSuccessUpdate,
    isError: isErrorUpdate,
    error: errorUpadte,
    isPending,
  } = useUpdateData(`${MRS_API}`);

  const handleInfoModalClose = () => {
    setIsInfoModalOpen(false);
    navigate('/stock-movement/material-request');
  };

  const handleClose = () => {
    navigate('/stock-movement/material-request');
  };

  const { data: defaultLocationData, isLoading: isDefaultLocationLoading } =
    useFetchData<PageableResponse<BaseModel>>('/location/locations', {
      resource: Resource.MIN,
      search: '',
      length: '999999',
    });

  useEffect(() => {
    if (data !== undefined) {
      data.movements.map((movement) => {
        movement.uuid = uuidv4();
      });
      materialRequestStore.setFormData(data);
    }
    if (isError) {
      setIsErrorModalOpen(true);
      setErrorMess(error.message);
    }
    if (isFileError) {
      setIsErrorModalOpen(true);
      setErrorMess(fileError.message);
    }
  }, [data, isError]);

  useEffect(() => {
    if (isSuccessUpdate) setIsInfoModalOpen(true);
    if (isErrorUpdate) {
      setIsErrorModalOpen(true);
      setErrorMess(errorUpadte?.message);
    }
  }, [isSuccessUpdate, isErrorUpdate]);

  const handleErrorModalClose = () => {
    setIsErrorModalOpen(false);
  };

  const handleDeleteFile = (file: any) => {
    mutationDelete.mutate(file.uid);
  };

  const onFinish = (values: any) => {
    const destination: BaseModel | undefined = defaultLocationData?.data.find(
      (obj) => {
        return obj.id == values.destination;
      },
    );

    const location: BaseModel | undefined = defaultLocationData?.data.find(
      (obj) => {
        return obj.id == values.location;
      },
    );

    const payload: MaterialRequestRecord = {
      ...values,
      id: params.id,
      code: data?.code,
      destination: destination,
      location: location,
      requiredDate: dayjs(values.requiredDate).format(dateFormat),
      issuedDate: dayjs(values.issuedDate).format(dateFormat),
      source: values.source,
      proderNo: values.proderNo,
      sku: values.sku,
      status: values.status,
      movements: materialRequestStore.formData.movements || [],
    };
    mutate({
      id: params.id,
      form: payload,
    });
    if (values.files !== undefined) {
      const fileList = values.files.fileList;
      const formData = new FormData();
      fileList.forEach((file: any) => {
        formData.append('files', file.originFileObj);
      });
      mutateFile({ id: params.id, formData: formData });
    }
  };

  const fileUploads = dataFiles?.map((file) => ({
    uid: file.id,
    name: file.origFilename,
    status: 'done',
    url: file.code,
  }));

  const fields: FormField[] = [
    {
      name: 'code',
      label: 'Code',
      type: 'input',
      disabled: true,
      defaultValue: data?.code,
    },
    {
      name: 'requiredDate',
      label: 'Required Date',
      rules: [{ required: true, message: 'Please select required date' }],
      type: 'datepicker',
      defaultValue: data?.requiredDate,
    },
    {
      name: 'location',
      label: 'Location',
      allowClear: true,
      type: 'select',
      isLoading: isDefaultLocationLoading,
      isMultiple: false,
      values: defaultLocationData?.data,
      rules: [{ required: true, message: 'Please select location' }],
      defaultValue: data?.location?.id,
    },
    {
      name: 'destination',
      label: 'Destination',
      allowClear: true,
      type: 'select',
      isLoading: isDefaultLocationLoading,
      isMultiple: false,
      values: defaultLocationData?.data,
      rules: [{ required: true, message: 'Please select destination' }],
      defaultValue: data?.destination?.id,
    },
    {
      name: 'source',
      label: 'Source Document',
      type: 'input',
      defaultValue: data?.source,
    },
    {
      name: 'notes',
      label: 'Remarks',
      type: 'textarea',
      defaultValue: data?.notes,
    },
    {
      name: 'status',
      label: 'Status',
      type: 'status',
      defaultValue: data?.status,
    },
    {
      name: 'files',
      label: 'Attachments',
      disabled: false,
      type: 'file',
      defaultValue: fileUploads,
    },
  ];

  return (
    <>
      <div className={'flex items-center justify-between'}>
        <PageHeaderAction action={'Request'} moduleName={'Material request'} />
        <Space>
          <Button onClick={handleClose}>Close</Button>
          <SubmitButton loading={isPending} form={form}>
            Submit
          </SubmitButton>
        </Space>
      </div>
      <div className="bg-white mt-4 h-[calc(84vh)] overflow-auto rounded-xl">
        {isLoading ? (
          <div className="p-4">
            <MaterialRequestLoadingView active={true} />
            <div className={'text-base font-semibold leading-6'}>
              Order Lines
            </div>
          </div>
        ) : (
          <div>
            <div className="p-4">
              <CustomForm
                onDeleteFile={handleDeleteFile}
                data={{ ...data, files: fileUploads }}
                formFields={fields}
                form={form}
                onFinish={onFinish}
                rowCol={3}
                layout={'vertical'}
                formName={'po_form_update'}
              />
            </div>
            <MaterialRequestMovementEdit />
          </div>
        )}
      </div>

      <ErrorModal
        visible={isErrorModalOpen}
        message={errorMess}
        onClose={handleErrorModalClose}
      />

      <InfoModal
        visible={isInfoModalOpen}
        message={'Material Request successfully updated'}
        onClose={handleInfoModalClose}
      />
    </>
  );
};

export default MaterialRequestFormEditPage;
